import {
  CalculatorIcon,
  Calendar1Icon,
  CheckIcon,
  Divider,
  Edit1Icon,
  GKButton,
  GKDatePicker,
  GKSearchSelect,
  Grid2,
  LabelIcon,
  ResetIcon,
  Show,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery } from "hooks/useQueries";
import { CalCulationHistory } from "interfaces/Revenue";
import DashboardLayout from "layouts/DashboardLayout";
import { isEmpty, map } from "lodash";
import { useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateFeeCalculationMutation,
  useMinesFeeMutation,
  useSaveFeeCalculationMutation,
} from "store/features/Revenue/FeeCalculationApi";
import { useAppSelector } from "store/store";
import { numberToCurrency } from "utils";
import { matchDate } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";
import { FeeAnnexureTable, FeeCard, feeSections } from "../utils";
import MinesFeeModal from "./MinesFeeModal";
import ProformaModal from "./ProformaModal";

const frequencyDays: Record<string, number> = {
  Monthly: 31,
  Quarterly: 92,
  "Semi-Annually": 183,
  Yearly: 366,
};

const CalculationForm = () => {
  useDocumentTitle("Fee Calculation");

  const navigate = useNavigate();
  const { portfolioId } = useParams();

  const portfolios = useAppSelector((state) => state?.app?.portfolios);

  const [openProformaModal, setOpenProformaModal] = useState(false);
  const [openMinesFeeModal, setOpenMinesFeeModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [lastFetchedDates, setLastFetchedDates] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({ startDate: null, endDate: null });

  const { data: feePlanList, isFetching: feePlanFetching } = useFetchQuery({
    key: ["GET_FEE_PLANS_DROPDOWN"],
    route: `/api/revenue/fee-plan/dropdown/`,
    requestBody: {
      revenue_products: JSON.stringify(["management_fee", "performance_fee"]),
    },
  });

  const form = useForm({
    defaultValues: {
      portfolio: portfolioId || "",
      managementFee: null,
      feePlans: {
        feePlan: "",
        startDate: "",
        endDate: "",
      },
      extraThings: {
        advanceDateSection: false,
        minesManagementFee: false,
        countDays: 365,
      },
      response: null,
    },
  });

  const [watchAdvanceDateSection, watchMinesManagementFee] = useWatch({
    control: form.control,
    name: ["extraThings.advanceDateSection", "extraThings.minesManagementFee"],
  }) as [boolean, boolean];

  const [createFeeCalculation, { isLoading, reset, data }] =
    useCreateFeeCalculationMutation();
  const [saveFeeCalculation, { isLoading: saveCalculationLoading }] =
    useSaveFeeCalculationMutation();
  const [minesFee, { data: minesFeeData, isLoading: minesFeeLoading }] =
    useMinesFeeMutation();

  const totalAmount = useMemo(() => {
    const selectedSum = minesFeeData?.data
      ?.filter((row) => selected.includes(row.id))
      ?.reduce((acc, curr) => acc + (curr.managementFeeAmount || 0), 0);

    return selectedSum + (form.getValues("managementFee") || 0);
  }, [selected, form.watch("managementFee"), minesFeeData]);

  const handleResetForm = () => {
    reset();
    form.reset({
      ...form.formState.defaultValues,
      portfolio: portfolioId || "",
    });
  };

  const handleDraftCalculation = async () => {
    try {
      await saveFeeCalculation(form.getValues("response")).unwrap();
      navigate(-1);
    } catch (err) {
      console.error("Save fee calculation failed:", err);
    }
  };

  const handleOpenMinesModal = async () => {
    setOpenMinesFeeModal(true);

    const startDate = form.getValues("feePlans.startDate");
    const endDate = form.getValues("feePlans.endDate");

    const isDateChanged =
      startDate !== lastFetchedDates.startDate ||
      endDate !== lastFetchedDates.endDate;

    if (!minesFeeData || isDateChanged) {
      try {
        await minesFee({
          portfolio: form.getValues("portfolio"),
          startDate: form.getValues("feePlans.startDate"),
          endDate: form.getValues("feePlans.endDate"),
        }).unwrap();

        setLastFetchedDates({ startDate, endDate });
      } catch (err) {
        console.error("Mines fee failed:", err);
      }
    }
  };

  return (
    <DashboardLayout title="Fee Calculation" disablePadding>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(async (values) => {
            try {
              const response = await createFeeCalculation({
                portfolio: values.portfolio,
                feePlans: [values?.feePlans],
                managementFee: totalAmount ?? 0,
              }).unwrap();

              form.setValue("response", response.data);
            } catch (err) {
              console.error("Create fee calculation failed:", err);
            }
          })}
        >
          <Grid2 container>
            <Grid2
              size={4}
              borderRight={`1px solid var(--neutral-100)`}
              p={3}
              height={"calc(100vh - 66px)"}
              overflow={"auto"}
              alignContent={"flex-start"}
            >
              <Grid2 container size={12} spacing={4}>
                <Grid2 size={12}>
                  <Typography
                    fontSize={12}
                    fontWeight={600}
                    color="var(--neutral-500)"
                  >
                    Calculate Fees
                  </Typography>
                </Grid2>
                <Grid2 size={12} mt={1}>
                  <Controller
                    name="portfolio"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select portfolio",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <GKSearchSelect
                          {...field}
                          requiredField
                          disableClearable={false}
                          inputLabel="Portfolio"
                          renderOption={(props, option) => (
                            <li
                              {...props}
                              key={props?.key}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <Typography fontWeight={600}>
                                {option.clientName}
                              </Typography>
                              <Typography>{option.name}</Typography>
                            </li>
                          )}
                          options={portfolios || []}
                          value={
                            portfolios.find(
                              (dataItem) => dataItem.value === +field.value,
                            )?.name || ""
                          }
                          onChange={(e, val) =>
                            field.onChange(val?.value || null)
                          }
                          getOptionLabel={(option) => option.name || option}
                          readOnly={!!portfolioId}
                          error={!!error}
                          helperText={error?.message}
                        />
                      );
                    }}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    name={`feePlans.feePlan`}
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select fee plan",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        requiredField
                        disableClearable={false}
                        inputLabel="Fee Plan"
                        loading={feePlanFetching}
                        options={feePlanList?.data?.data || []}
                        onChange={(e, val) => {
                          field.onChange(val?.value ?? "");
                          form.setValue(
                            "extraThings.advanceDateSection",
                            val?.advanceDateSection,
                          );
                          form.setValue(
                            "extraThings.minesManagementFee",
                            val?.minesManagementFee,
                          );
                          form.setValue("managementFee", null);
                          const billingFrequency =
                            val?.billingFrequency?.trim();
                          form.setValue(
                            "extraThings.countDays",
                            frequencyDays[billingFrequency] ?? 365,
                          );
                          setSelected([]);
                        }}
                        value={
                          feePlanList?.data?.data?.find(
                            (data: { value: string | number }) =>
                              data?.value === field.value,
                          )?.name
                        }
                        getOptionLabel={(option) => option.name || option}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    control={form.control}
                    name={`feePlans.startDate`}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select start date",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <GKDatePicker
                          {...field}
                          requiredField
                          inputLabel="Start Date"
                          value={dayjs(field.value)}
                          onChange={(value) => {
                            field.onChange(dayjs(value).format("YYYY-MM-DD"));
                          }}
                          disableToday={!watchAdvanceDateSection}
                          disableFuture={!watchAdvanceDateSection}
                          slotProps={{
                            textField: {
                              error: !!error,
                              helperText: error?.message,
                            },
                          }}
                        />
                      );
                    }}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    control={form.control}
                    name={`feePlans.endDate`}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select end date",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <GKDatePicker
                          {...field}
                          requiredField
                          inputLabel="End Date"
                          value={dayjs(field.value)}
                          onChange={(value) => {
                            field.onChange(dayjs(value).format("YYYY-MM-DD"));
                          }}
                          disableToday={!watchAdvanceDateSection}
                          disableFuture={!watchAdvanceDateSection}
                          maxDate={dayjs(form.watch("feePlans.startDate")).add(
                            form.watch("extraThings.countDays"),
                            "days",
                          )}
                          slotProps={{
                            textField: {
                              error: !!error,
                              helperText: error?.message,
                            },
                          }}
                        />
                      );
                    }}
                  />
                </Grid2>

                <Show.When
                  isTrue={!!watchMinesManagementFee && selected.length === 0}
                >
                  <Grid2
                    container
                    size={12}
                    p={2}
                    bgcolor={"var(--grey-bg-100)"}
                    borderRadius={2}
                    spacing={2}
                  >
                    <Grid2 size={12}>
                      <Typography
                        fontSize={13}
                        fontWeight={600}
                        color="var(--black-900)"
                      >
                        Want to exclude management fees(collected during the
                        above period) from the performance fees calculation?
                      </Typography>
                      <Typography
                        fontSize={12}
                        fontWeight={500}
                        color="var(--neutral-500)"
                      >
                        Skip this step if you don&apos;t want to exclude
                        management fees.
                      </Typography>
                    </Grid2>
                    <Grid2
                      size={12}
                      display={"flex"}
                      justifyContent={"flex-end"}
                    >
                      <GKButton variant="text" onClick={handleOpenMinesModal}>
                        Yes, exclude management fees!
                      </GKButton>
                    </Grid2>
                  </Grid2>
                </Show.When>

                <Show.When
                  isTrue={!!watchMinesManagementFee && selected.length !== 0}
                >
                  <Grid2
                    container
                    size={12}
                    p={2}
                    bgcolor={"var(--grey-bg-100)"}
                    borderRadius={2}
                    spacing={2}
                  >
                    <Grid2 size={12}>
                      <Typography
                        fontSize={13}
                        fontWeight={600}
                        color="var(--black-900)"
                      >
                        Past management fees exclusion:
                      </Typography>
                    </Grid2>
                    <Grid2 container size={12} spacing={1}>
                      <Grid2 container size={12}>
                        <Grid2 size={5}>
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="var(--neutral-500)"
                          >
                            Management Fees:
                          </Typography>
                        </Grid2>
                        <Grid2 size={7}>
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="var(--neutral-900)"
                          >
                            {numberToCurrency(totalAmount)}
                          </Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={5}>
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="var(--neutral-500)"
                          >
                            No. of Invoices:
                          </Typography>
                        </Grid2>
                        <Grid2 size={7}>
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="var(--neutral-900)"
                          >
                            {selected?.length}
                          </Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={5}>
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="var(--neutral-500)"
                          >
                            Date Range:
                          </Typography>
                        </Grid2>
                        <Grid2 size={7}>
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="var(--neutral-900)"
                          >
                            {form.getValues("feePlans.startDate")} -
                            {form.getValues("feePlans.endDate")}
                          </Typography>
                        </Grid2>
                      </Grid2>
                    </Grid2>
                    <Grid2
                      size={12}
                      display={"flex"}
                      justifyContent={"flex-end"}
                    >
                      <GKButton
                        variant="text"
                        onClick={handleOpenMinesModal}
                        startIcon={<Edit1Icon />}
                      >
                        Edit Details
                      </GKButton>
                    </Grid2>
                  </Grid2>
                </Show.When>

                <Grid2
                  size={12}
                  mt={1}
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                >
                  <GKButton
                    fullWidth
                    variant="outlined"
                    color="dark"
                    startIcon={<ResetIcon />}
                    loading={saveCalculationLoading}
                    onClick={handleResetForm}
                  >
                    Reset all
                  </GKButton>
                  <GKButton
                    fullWidth
                    variant="contained"
                    startIcon={<CalculatorIcon />}
                    type="submit"
                    loading={isLoading}
                  >
                    Calculate Fee
                  </GKButton>
                </Grid2>
              </Grid2>
            </Grid2>

            <Show.When isTrue={!!data?.data}>
              {map(data?.data?.feePlans, (item: CalCulationHistory) => {
                const visibleFeeSections = feeSections.filter(({ key }) => {
                  const feeData = item?.[key];
                  return !isEmpty(feeData);
                });

                const gridSize =
                  visibleFeeSections.length > 0
                    ? Math.floor(12 / visibleFeeSections.length)
                    : visibleFeeSections.length > 1
                      ? 6
                      : 12;

                const annexureSections = visibleFeeSections.filter(
                  ({ key }) => {
                    const feeData = item?.[key];
                    if (!feeData || feeData.annexure?.length === 0)
                      return false;
                    if (
                      key === "managementFee" &&
                      feeData?.feeType === "Flat Fee"
                    )
                      return false;
                    return true;
                  },
                );

                return (
                  <Grid2 size={8} key={item.id}>
                    <Grid2
                      container
                      size={12}
                      p={3}
                      borderBottom={`1px solid var(--neutral-100)`}
                    >
                      <Grid2 size={12}>
                        <Typography
                          fontSize={12}
                          fontWeight={600}
                          color="var(--neutral-500)"
                          textTransform={"uppercase"}
                        >
                          Summary details
                        </Typography>
                      </Grid2>
                      <Grid2 container size={12} spacing={"12px"} py={3}>
                        <Grid2 size={12}>
                          <Typography
                            display={"flex"}
                            alignItems={"center"}
                            gap={0.5}
                            fontWeight={500}
                            color="var(--neutral-500)"
                          >
                            <LabelIcon /> Plan Name :
                            <Typography fontWeight={500} color="var(--black)">
                              {item?.name || "-"}
                            </Typography>
                          </Typography>
                        </Grid2>
                        <Grid2 size={12}>
                          <Typography
                            display={"flex"}
                            alignItems={"center"}
                            gap={0.5}
                            fontWeight={500}
                            color="var(--neutral-500)"
                          >
                            <Calendar1Icon /> Period :
                            <Typography fontWeight={500} color="var(--black)">
                              {matchDate(item?.startDate, "DD/MM/YYYY")} -
                              {matchDate(item?.endDate, "DD/MM/YYYY")}
                            </Typography>
                          </Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2
                        container
                        size={12}
                        display={"flex"}
                        spacing={"12px"}
                      >
                        {visibleFeeSections.map(({ key, label }) => {
                          const feeData = item?.[key];

                          return (
                            <Grid2 key={key} size={gridSize}>
                              <FeeCard label={label} feeData={feeData} />
                            </Grid2>
                          );
                        })}
                      </Grid2>
                    </Grid2>

                    {annexureSections.length > 0 && (
                      <Grid2
                        container
                        size={12}
                        spacing={2}
                        p={3}
                        maxHeight={"calc(100vh - 399px)"}
                        minHeight={"calc(100vh - 399px)"}
                        overflow={"auto"}
                        alignContent={"flex-start"}
                      >
                        <Grid2 size={12}>
                          <Typography
                            fontSize={12}
                            fontWeight={600}
                            color="var(--neutral-500)"
                            textTransform={"uppercase"}
                          >
                            calculation details
                          </Typography>
                        </Grid2>
                        <Grid2 container size={12}>
                          {visibleFeeSections.map(({ key, label }) => {
                            const feeData = item?.[key];

                            if (!feeData || feeData.annexure?.length === 0)
                              return null;

                            if (
                              key === "managementFee" &&
                              feeData?.feeType === "Flat Fee"
                            )
                              return null;

                            return (
                              <Grid2 size={12}>
                                <FeeAnnexureTable
                                  type={key}
                                  label={label}
                                  feeData={feeData}
                                />
                              </Grid2>
                            );
                          })}
                        </Grid2>
                      </Grid2>
                    )}

                    <Grid2 container size={12}>
                      <Grid2 size={12}>
                        <Divider />
                      </Grid2>
                      <Grid2
                        size={12}
                        display={"flex"}
                        gap={1}
                        justifyContent={"flex-end"}
                        px={3}
                        py={2}
                      >
                        <GKButton
                          variant="outlined"
                          color="dark"
                          onClick={handleDraftCalculation}
                        >
                          Save as Draft
                        </GKButton>
                        <GKButton
                          variant="contained"
                          onClick={() => setOpenProformaModal(true)}
                          startIcon={<CheckIcon />}
                        >
                          Save & Create Proforma
                        </GKButton>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                );
              })}
            </Show.When>
          </Grid2>
        </form>

        <Show.When isTrue={openMinesFeeModal}>
          <MinesFeeModal
            open={openMinesFeeModal}
            setOpen={setOpenMinesFeeModal}
            selected={selected}
            setSelected={setSelected}
            minesFeeData={minesFeeData?.data}
            loading={minesFeeLoading}
            totalAmount={totalAmount ?? 0}
          />
        </Show.When>
      </FormProvider>

      <Show.When isTrue={openProformaModal}>
        <ProformaModal
          open={openProformaModal}
          setOpen={setOpenProformaModal}
          calculationRowData={form.getValues("response")}
          isDirectProforma
        />
      </Show.When>
    </DashboardLayout>
  );
};

export default CalculationForm;
